import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useInboxStore, type Conversation, type TeamMember } from '../../stores/inbox-store';
import { useAuthStore } from '../../stores/auth-store';

/**
 * Toewijs-picker in de thread-header. Gesloten: pill met eigenaar-initialen
 * of "Toewijzen". Open: dropdown met "Aan mij" bovenaan, overige teamleden,
 * en "Niet toegewezen" om te ontwijzen.
 */

function fallbackDisplayName(email: string): string {
  return (email.split('@')[0] ?? email)
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Resolve email → displayName via teamMembers, fallback email-prefix. */
export function resolveAssigneeName(email: string, teamMembers: TeamMember[]): string {
  const member = teamMembers.find((m) => m.email === email.toLowerCase());
  return member?.displayName || fallbackDisplayName(email);
}

/** Max 2 letters uit de displayName (bv. "Willem Blok" → "WB"). */
export function assigneeInitials(email: string, teamMembers: TeamMember[]): string {
  const name = resolveAssigneeName(email, teamMembers);
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}

export function AssigneePicker({ conversation }: { conversation: Conversation }) {
  const { teamMembers, fetchTeam, assignConversation } = useInboxStore();
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void fetchTeam();
  }, [fetchTeam]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const assignee = conversation.assigned_to?.toLowerCase() ?? null;
  const myEmail = user?.email?.toLowerCase() ?? null;

  const handleAssign = (email: string | null) => {
    setOpen(false);
    setError(null);
    if ((email ?? null) === assignee) return;
    assignConversation(conversation.id, email).catch((err) => {
      setError(err instanceof Error ? err.message : 'Toewijzen mislukt');
    });
  };

  // "Aan mij" bovenaan, daarna de rest alfabetisch (al gesorteerd door API)
  const me = myEmail ? teamMembers.find((m) => m.email === myEmail) : undefined;
  const others = teamMembers.filter((m) => m.email !== myEmail);

  return (
    <div className="relative flex-shrink-0" ref={containerRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        title={assignee ? `Toegewezen aan ${resolveAssigneeName(assignee, teamMembers)}` : 'Toewijzen aan collega'}
        className={clsx(
          'flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-md transition-colors',
          assignee
            ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
            : 'border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700'
        )}
      >
        {assignee ? (
          <>
            <span className="w-5 h-5 rounded-full bg-indigo-600 text-white text-[10px] font-semibold flex items-center justify-center">
              {assigneeInitials(assignee, teamMembers)}
            </span>
            <span className="hidden xl:inline">{resolveAssigneeName(assignee, teamMembers)}</span>
          </>
        ) : (
          <>
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z" />
            </svg>
            <span className="hidden xl:inline">Toewijzen</span>
          </>
        )}
      </button>

      {error && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-red-50 border border-red-200 text-red-700 text-xs px-2 py-1 rounded-md whitespace-nowrap">
          {error}
        </div>
      )}

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1">
          {me && (
            <button
              onClick={() => handleAssign(me.email)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-700 hover:bg-slate-50"
            >
              <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                {assigneeInitials(me.email, teamMembers)}
              </span>
              <span className="flex-1 truncate">Aan mij ({me.displayName})</span>
              {assignee === me.email && (
                <svg className="w-4 h-4 text-indigo-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          )}
          {others.map((member) => (
            <button
              key={member.email}
              onClick={() => handleAssign(member.email)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-700 hover:bg-slate-50"
            >
              <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                {assigneeInitials(member.email, teamMembers)}
              </span>
              <span className="flex-1 truncate">{member.displayName}</span>
              {assignee === member.email && (
                <svg className="w-4 h-4 text-indigo-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
          {teamMembers.length === 0 && (
            <div className="px-3 py-2 text-sm text-slate-400">Teamleden laden...</div>
          )}
          {assignee && (
            <>
              <div className="border-t border-slate-100 my-1" />
              <button
                onClick={() => handleAssign(null)}
                className="w-full px-3 py-2 text-sm text-left text-slate-500 hover:bg-slate-50"
              >
                Niet toegewezen
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
